import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { SelectedSeriesPoint } from 'pages/workbench/types';
import { mapBurstDataToPoints } from 'pages/workbench/workbench_tabs/trend/frequency_domain/shared';
import { useMemo } from 'react';
import { Point } from 'shared/chart/core/types';
import { sortBy } from 'utils/array';
import { queryClient } from 'utils/query';

const queryKey = 'trend-waterfall-data';

export function invalidateWaterfallFrequencyData() {
    queryClient.invalidateQueries({
        queryKey: [queryKey],
    });
}

export type TrendWaterfallFrequencyData = Readonly<{
    seriesId: string;
    timestamp: Date;
    seriesUnit?: string;
    dataPoints: Point[];
    seriesMinY: number;
    seriesMaxY: number;
    isVibrationType: boolean;
}>;

export type UseTrendWaterfallFrequencyDataOptions = Readonly<{
    selectedPoint: SelectedSeriesPoint;
    waterfallFrequencyPoints: SelectedSeriesPoint[];
    returnVelocity?: boolean;
}>;

export function useTrendWaterfallFrequencyData({
    selectedPoint,
    waterfallFrequencyPoints,
    returnVelocity,
}: UseTrendWaterfallFrequencyDataOptions) {
    const points = useMemo(
        () =>
            sortBy([selectedPoint, ...waterfallFrequencyPoints], (point) => point.xValue),
        [selectedPoint, waterfallFrequencyPoints]
    );

    const pointsKey = useMemo(
        () => points.map((point) => `${point.seriesId}-${point.xValue}`).join(','),
        [points]
    );

    return useQuery({
        queryKey: [queryKey, selectedPoint.seriesId, pointsKey, returnVelocity],

        queryFn: () =>
            Promise.all(
                points.map(async (point) => ({
                    point,
                    data: await sdClient.getBurstData({
                        indicatorId: point.seriesId,
                        includeBaseline: false,
                        returnTimeDomain: false,
                        returnVelocity: returnVelocity ?? false,
                        dateTimestamp: new Date(point.xValue),
                    }),
                }))
            ),

        placeholderData: keepPreviousData,

        select: (response) =>
            response.map<TrendWaterfallFrequencyData>(({ point, data }) => {
                const burstData = data.BurstData[0];
                const dataPoints = mapBurstDataToPoints(burstData);
                const mappedYs = dataPoints.map(({ y }) => y);
                const isVibrationType = data.IsVibrationTypeIndicator;

                return {
                    seriesId: point.seriesId,
                    timestamp: new Date(point.xValue),
                    seriesUnit: burstData.Units,
                    seriesMinY: Math.min(...mappedYs),
                    seriesMaxY: Math.max(...mappedYs),
                    dataPoints,
                    isVibrationType,
                };
            }),
    });
}
